/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
/* eslint-disable react-hooks/set-state-in-effect, react-hooks/purity --
 * v77.99.75: Admin operator dashboard. Heavy use of "fetch on filter
 * change" useEffects with setState in async IIFEs, useMemo blocks
 * that read from the navigate hook for side-effects on row-click, etc.
 * eslint-plugin-react-hooks v5's experimental rules are over-eager
 * for these admin-only patterns. Other lints stay enabled.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, Gift, UserCog, X, Clock, Infinity as InfinityIcon, Plus, Trash2, RefreshCw, UserX, ShieldAlert, MailX, MailCheck, MailWarning, AlertTriangle, Repeat, KeyRound, History, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDT } from "@/lib/format";
import { AccountTimelineModal, UserGdprControls } from "@/components/AdminPhase5";

// Dedicated admin-facing surface for granting, extending, and revoking
// Premium access without touching Stripe. Reuses the existing
// `/api/admin/complimentary*` endpoints that were built in Phase 1 — they
// already bypass billing and write to the audit log, so this section is
// pure UX glue.
export const AdminAccessSection = () => {
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const viewerIsPrimary = !!user?.is_primary_admin;
  const [query, setQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("all");       // plan tier
  const [sourceFilter, setSourceFilter] = useState("all");
  // v77.99.10 — Plan-interval filter (monthly | yearly | all). Sent to
  // GET /admin/users?plan_interval=…
  const [planIntervalFilter, setPlanIntervalFilter] = useState("all");   // stripe / admin / trial
  // v77.99 — Anti-abuse filter chip. Cycles through:
  //   all | unverified | trial | expired_trial | suspicious |
  //   admin_granted | premium | payment_failed
  const [abuseFilter, setAbuseFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openGrant, setOpenGrant] = useState(null);   // user being granted
  const [openExtend, setOpenExtend] = useState(null); // user being extended
  const [openImpersonate, setOpenImpersonate] = useState(null); // user being impersonated
  const [openTimeline, setOpenTimeline] = useState(null); // user whose timeline is being viewed
  // v77.99.115 — Admin "Payment integrity" remediation modal. Holds the
  // user whose household is being audited for double-counted expenses
  // (the iter114 bug). Drives the dry-run → review → apply flow.
  const [openIntegrity, setOpenIntegrity] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      if (planFilter !== "all") params.set("plan", planFilter);
      if (sourceFilter !== "all") params.set("source", sourceFilter);
      if (planIntervalFilter !== "all") params.set("plan_interval", planIntervalFilter);
      if (abuseFilter !== "all") params.set("filter", abuseFilter);
      params.set("limit", "50");
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setRows(data.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load users");
    } finally {
      setBusy(false);
    }
  };

  // Kick off an initial load; re-fetch whenever filters change. The text
  // search requires the user to press Enter / click Search to avoid
  // hammering the API on every keystroke.
  useEffect(() => { load(); }, [planFilter, sourceFilter, planIntervalFilter, abuseFilter]);

  return (
    <section className="space-y-3" data-testid="admin-access-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5">
          <UserCog className="w-3.5 h-3.5" /> User access &amp; subscriptions
        </h2>
        <div className="text-xs text-[#6F828A] font-mono-data tabular-nums">
          {rows.length.toLocaleString()} match{rows.length === 1 ? "" : "es"}
        </div>
      </div>

      {/* Filter / search row */}
      <div className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-3">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Search by email or name</label>
            <div className="relative">
              <Search className="w-4 h-4 text-[#6F828A] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. alex@example.com or Alex Parent"
                className="pl-8"
                data-testid="admin-access-search"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Plan</label>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-[160px]" data-testid="admin-access-plan-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All plans</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="complimentary">Admin-granted</SelectItem>
                <SelectItem value="admin">Admin account</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Source</label>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[160px]" data-testid="admin-access-source-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="stripe">Stripe</SelectItem>
                <SelectItem value="admin">Admin grant</SelectItem>
                <SelectItem value="trial">Trial / none</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* v77.99.10 — Plan-interval filter (monthly | yearly). */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Cadence</label>
            <Select value={planIntervalFilter} onValueChange={setPlanIntervalFilter}>
              <SelectTrigger className="w-[140px]" data-testid="admin-access-plan-interval-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cadences</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={busy} className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm" data-testid="admin-access-search-submit">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Search"}
          </Button>
        </form>
        {/* v77.99 — Anti-abuse filter chips. Mutually exclusive (one
            chip at a time). Reset to "all" by clicking the active
            chip a second time. */}
        <div className="flex flex-wrap gap-2 pt-1" data-testid="admin-access-abuse-chips">
          {[
            { key: "all", label: "All", icon: null },
            { key: "unverified", label: "Unverified", icon: MailX },
            { key: "trial", label: "Trial", icon: Clock },
            { key: "expired_trial", label: "Expired trial", icon: Clock },
            { key: "suspicious", label: "Suspicious", icon: ShieldAlert },
            { key: "admin_granted", label: "Admin-granted", icon: Gift },
            { key: "premium", label: "Premium", icon: Gift },
            { key: "payment_failed", label: "Failed payments", icon: AlertTriangle },
          ].map(({ key, label, icon: Icon }) => {
            const active = abuseFilter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setAbuseFilter(active && key !== "all" ? "all" : key)}
                className={`inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-full border transition-colors ${active
                  ? "bg-[#1F9FA4] text-white border-[#1F9FA4]"
                  : "bg-white text-[#2E3A3F] border-[#D9E1E3] hover:bg-[#EEF3F4]"}`}
                data-testid={`admin-access-chip-${key}`}
              >
                {Icon && <Icon className="w-3 h-3" />}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Results table */}
      <div className="border border-[#D9E1E3] bg-white rounded-md overflow-hidden" data-testid="admin-access-results">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#6F828A]">No users match the current filters.</div>
        ) : (
          <ul className="divide-y divide-[#E2E8EC]">
            {rows.map((u) => (
              <UserRow
                key={u.id}
                u={u}
                viewerIsPrimary={viewerIsPrimary}
                viewerId={user?.id}
                onGrant={() => setOpenGrant(u)}
                onExtend={() => setOpenExtend(u)}
                onImpersonate={() => setOpenImpersonate(u)}
                onTimeline={() => setOpenTimeline(u)}
                onIntegrity={() => setOpenIntegrity(u)}
                onChanged={load}
                onRevoke={async () => {
                  if (!window.confirm(`Revoke admin-granted Premium for ${u.email}?`)) return;
                  try {
                    await api.delete(`/admin/complimentary?email=${encodeURIComponent(u.email)}`);
                    toast.success("Premium access revoked");
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Couldn't revoke"); }
                }}
                onDelete={async (cascade) => {
                  const label = cascade
                    ? `Permanently delete ${u.email} AND their household data (messages, children, expenses, etc. if they're the only member)? This cannot be undone.`
                    : `Permanently delete the user ${u.email}? Their household data will be kept so their co-parent can still use it. This cannot be undone.`;
                  if (!window.confirm(label)) return;
                  try {
                    const { data } = await api.delete(`/admin/users/${u.id}?cascade=${cascade}`);
                    const total = Object.values(data.deleted || {}).reduce((a, b) => a + (b || 0), 0);
                    toast.success(`Deleted ${u.email} (${total} rows across ${Object.keys(data.deleted).length} collections)`);
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                }}
              />
            ))}
          </ul>
        )}
      </div>

      <GrantDialog
        user={openGrant}
        onClose={() => setOpenGrant(null)}
        onDone={() => { setOpenGrant(null); load(); }}
      />
      <ExtendDialog
        user={openExtend}
        onClose={() => setOpenExtend(null)}
        onDone={() => { setOpenExtend(null); load(); }}
      />
      <ImpersonateDialog
        user={openImpersonate}
        onClose={() => setOpenImpersonate(null)}
        onConfirmed={async (reason) => {
          try {
            await startImpersonation({ user_id: openImpersonate.id, reason });
            toast.success(`Now acting as ${openImpersonate.email}`);
            setOpenImpersonate(null);
            // Send the admin straight into the parent dashboard so they
            // can immediately reproduce the user's view.
            navigate("/app/dashboard");
          } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't start impersonation");
          }
        }}
      />
      <AccountTimelineModal
        user={openTimeline}
        open={!!openTimeline}
        onClose={() => setOpenTimeline(null)}
      />
      <PaymentIntegrityModal
        user={openIntegrity}
        open={!!openIntegrity}
        onClose={() => setOpenIntegrity(null)}
      />
    </section>
  );
};

const PLAN_LABELS = {
  paid: { label: "Paid", bg: "bg-[#DCEFDC]", fg: "text-[#1E5A1E]", border: "border-[#A9D3A9]" },
  trial: { label: "Trial", bg: "bg-[#FDF4E2]", fg: "text-[#8F6B1F]", border: "border-[#E6CFA2]" },
  free: { label: "Free", bg: "bg-[#F0EFEE]", fg: "text-[#6F828A]", border: "border-[#CFD6D8]" },
  complimentary: { label: "Admin-granted", bg: "bg-[#ECE3F3]", fg: "text-[#4E3877]", border: "border-[#C5B2DE]" },
  admin: { label: "Admin", bg: "bg-[#D6F0EF]", fg: "text-[#116B6E]", border: "border-[#A7D8DA]" },
};

const UserRow = ({ u, viewerIsPrimary, viewerId, onGrant, onExtend, onRevoke, onDelete, onImpersonate, onTimeline, onIntegrity, onChanged }) => {
  const pill = PLAN_LABELS[u.plan] || PLAN_LABELS.free;
  const hasAdminGrant = u.subscription_source === "admin";
  const lifetime = hasAdminGrant && !u.complimentary?.expires_at;
  // Delete is only exposed to the primary admin and never for accounts that
  // carry any admin privilege (primary, env, or granted). Backend enforces
  // the same rules — the UI just hides the button to avoid confusing
  // no-op clicks.
  const canDelete = viewerIsPrimary && !u.is_admin && !u.is_env_admin && !u.is_primary_admin;
  // v77.99 — Impersonation is offered for any non-admin, non-self user.
  // Backend re-enforces these rules so the UI just hides the button to
  // prevent guaranteed-failure clicks.
  const canImpersonate = !u.is_admin && !u.is_env_admin && !u.is_primary_admin
    && u.role !== "mediator" && u.id !== viewerId;
  const flags = u.flags || [];

  return (
    <li
      className="px-4 py-4 sm:px-5 sm:py-5"
      data-testid={`admin-access-row-${u.id}`}
    >
      {/* Mobile-first stacked card. The desktop layout (sm:flex-row)
          re-uses the same flex tree but tightens spacing. We deliberately
          DON'T put name+email+badges into a single wrap-line because that
          collapses awkwardly under 480px — instead each band has its own
          wrap context with consistent `gap-2`. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-5">
        {/* === Identity + status block === */}
        <div className="min-w-0 flex-1">
          {/* Band 1: name + plan pill on a single line on every viewport.
              `flex-wrap` only kicks in when the name is exceptionally long. */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-semibold text-[15px] text-[#2E3A3F] break-words">
              {u.name || "(unnamed)"}
            </span>
            <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border whitespace-nowrap ${pill.bg} ${pill.fg} ${pill.border}`}>
              {pill.label}
            </span>
          </div>
          {/* Band 2: email — full width on mobile so it never butts into a
              button. `break-all` for very long addresses. */}
          <div className="text-[12.5px] text-[#6F828A] font-mono-data break-all mt-0.5">
            {u.email}
          </div>
          {/* Band 3: badge cluster. Wraps cleanly with consistent gap.
              `[&>*]:whitespace-nowrap` keeps individual badges from wrapping
              their own contents (e.g. "Verification pending" shouldn't
              break across two lines). */}
          <div className="flex flex-wrap gap-1.5 mt-2 [&>*]:whitespace-nowrap">
            {hasAdminGrant && (
              <span
                className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border bg-[#ECE3F3] text-[#4E3877] border-[#C5B2DE]"
                data-testid={`admin-access-row-granted-${u.id}`}
              >
                Admin Granted
              </span>
            )}
            {u.subscription_status === "past_due" && (
              <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border bg-[#FBEFED] text-[#8C2A22] border-[#E8B8B2]">
                Past due
              </span>
            )}
            {flags.includes("email_verified") && (
              <FlagBadge tone="green" icon={MailCheck} label="Verified" testid={`flag-verified-${u.id}`} />
            )}
            {flags.includes("verification_pending") && (
              <FlagBadge tone="amber" icon={MailWarning} label="Verification pending" testid={`flag-pending-${u.id}`} />
            )}
            {flags.includes("email_unverified") && (
              <FlagBadge tone="red" icon={MailX} label="Unverified" testid={`flag-unverified-${u.id}`} />
            )}
            {flags.includes("disposable_email") && (
              <FlagBadge tone="red" icon={MailX} label="Disposable email" testid={`flag-disposable-${u.id}`} />
            )}
            {flags.includes("trial_abuse_flagged") && (
              <FlagBadge tone="red" icon={Repeat} label={`Trial abuse (${u.suspicious_signup_count})`} testid={`flag-trial-abuse-${u.id}`} />
            )}
            {flags.includes("suspicious_activity") && (
              <FlagBadge tone="red" icon={ShieldAlert} label="Suspicious" testid={`flag-suspicious-${u.id}`} />
            )}
            {flags.includes("payment_failed") && (
              <FlagBadge tone="red" icon={AlertTriangle} label="Payment failed" testid={`flag-payment-${u.id}`} />
            )}
          </div>
          {/* Band 4: meta facts. Fixed `min-w-0` parent + `whitespace-nowrap`
              on each pill prevents the per-character vertical wrap that
              the previous layout suffered from when a date string hit a
              narrow column. */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[12px] text-[#6F828A]">
            <span className="whitespace-nowrap">
              Source:{" "}
              <span className="text-[#2E3A3F] capitalize">{u.subscription_source}</span>
            </span>
            {u.access_expires_at && (
              <span className="whitespace-nowrap">
                Access until{" "}
                <span className="text-[#2E3A3F] font-mono-data">{formatDT(u.access_expires_at)}</span>
              </span>
            )}
            {lifetime && (
              <span className="inline-flex items-center gap-1 text-[#4E3877] whitespace-nowrap">
                <InfinityIcon className="w-3 h-3" /> Lifetime
              </span>
            )}
            {u.trial_end_at && u.plan === "trial" && (
              <span className="whitespace-nowrap">
                Trial ends{" "}
                <span className="font-mono-data">{formatDT(u.trial_end_at)}</span>
              </span>
            )}
            {u.plan === "paid" && (
              <>
                <span className="whitespace-nowrap" data-testid={`admin-access-row-plan-interval-${u.id}`}>
                  Cadence:{" "}
                  <span className={`font-medium capitalize ${u.plan_interval === "yearly" ? "text-[#0B4D50]" : "text-[#2E3A3F]"}`}>
                    {u.plan_interval || "monthly"}
                  </span>
                </span>
                <span className="whitespace-nowrap">
                  Payments:{" "}
                  <span className="font-mono-data tabular-nums">{u.payments_count}</span>
                </span>
              </>
            )}
          </div>
          {/* Band 5: secondary meta (collapsible details). On mobile,
              "View details" reveals last login, household count, signup
              IP, fingerprint hash — kept hidden by default to keep the
              card compact. On desktop the same info shows inline. */}
          {(u.last_login_at || u.household_membership_count > 1 || u.signup_ip || u.signup_fingerprint_hash) && (
            <details className="mt-2 group" data-testid={`admin-access-details-${u.id}`}>
              <summary className="text-[11px] text-[#1F9FA4] cursor-pointer select-none hover:underline list-none flex items-center gap-1 [&::-webkit-details-marker]:hidden">
                <span className="group-open:hidden">View details</span>
                <span className="hidden group-open:inline">Hide details</span>
              </summary>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[12px] text-[#6F828A]">
                {u.last_login_at && (
                  <span className="whitespace-nowrap">
                    Last login{" "}
                    <span className="font-mono-data">{formatDT(u.last_login_at)}</span>
                  </span>
                )}
                {u.last_login_ip && (
                  <span className="whitespace-nowrap">
                    IP <span className="font-mono-data">{u.last_login_ip}</span>
                  </span>
                )}
                {u.household_membership_count > 1 && (
                  <span className="whitespace-nowrap">
                    Households{" "}
                    <span className="font-mono-data tabular-nums">{u.household_membership_count}</span>
                  </span>
                )}
                {u.signup_ip && (
                  <span className="whitespace-nowrap">
                    Signup IP <span className="font-mono-data">{u.signup_ip}</span>
                  </span>
                )}
              </div>
            </details>
          )}
        </div>

        {/* === Action block ===
            Mobile: full-width bands stacked under the identity block,
            with primary actions in band 1 and destructive actions in
            band 2. Each button is min-h-9 (36px) for comfortable touch.
            Desktop (sm+): collapses to a right-aligned wrap row. */}
        <div className="flex flex-col gap-2 sm:items-end sm:flex-shrink-0 sm:min-w-[200px]">
          {/* Primary actions */}
          <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:flex-wrap sm:justify-end sm:w-auto">
            {!hasAdminGrant ? (
              <Button
                type="button"
                onClick={onGrant}
                className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm h-9 text-[13px] col-span-2 sm:col-auto"
                data-testid={`admin-access-grant-${u.id}`}
              >
                <Gift className="w-3.5 h-3.5 mr-1.5" /> Grant Premium
              </Button>
            ) : (
              <>
                {!lifetime && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onExtend}
                    className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4] rounded-sm h-9 text-[13px]"
                    data-testid={`admin-access-extend-${u.id}`}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Extend
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={onRevoke}
                  className="border-[#E8B8B2] text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm h-9 text-[13px]"
                  data-testid={`admin-access-revoke-${u.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Revoke
                </Button>
              </>
            )}
            {canImpersonate && (
              <Button
                type="button"
                variant="outline"
                onClick={onImpersonate}
                className="border-[#C5B2DE] text-[#4E3877] hover:bg-[#ECE3F3] rounded-sm h-9 text-[13px] col-span-2 sm:col-auto"
                data-testid={`admin-access-impersonate-${u.id}`}
                title="Open this user's account in admin impersonation mode (audit-logged)."
              >
                <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Impersonate
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={onTimeline}
              className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F8FAFB] rounded-sm h-9 text-[13px]"
              data-testid={`admin-access-timeline-${u.id}`}
              title="View this account's chronological event timeline."
            >
              <History className="w-3.5 h-3.5 mr-1.5" /> Timeline
            </Button>
            {/* v77.99.115 — Payment-integrity remediation. Opens a
                modal that scans this user's household for the iter114
                double-credit bug (expenses with both a credit
                allocation AND a live direct payment) and lets the
                admin dry-run / apply a fix that voids the duplicate
                direct payments. Fully audit-logged server-side. */}
            <Button
              type="button"
              variant="outline"
              onClick={onIntegrity}
              className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F8FAFB] rounded-sm h-9 text-[13px]"
              data-testid={`admin-access-integrity-${u.id}`}
              title="Scan this user's expenses for double-counted payments and remediate."
            >
              <Wrench className="w-3.5 h-3.5 mr-1.5" /> Payment integrity
            </Button>
          </div>
          {/* Phase 5 — inline GDPR controls. Collapsed by default so
              the action region stays compact on phones; opens a tiny
              row of three buttons (Export / Soft-delete / Restore). */}
          <UserGdprControls user={u} onChanged={onChanged} />
          {/* Secondary destructive band — visually separated by a thin
              divider so an admin can't fat-finger Delete while reaching
              for Grant/Extend. */}
          {canDelete && (
            <div className="flex gap-2 w-full sm:w-auto pt-1 sm:pt-0 border-t border-[#F0EFEE] sm:border-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onDelete(false)}
                className="flex-1 sm:flex-none border-[#E8B8B2] text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm h-9 text-[13px]"
                data-testid={`admin-access-delete-${u.id}`}
                title="Delete this user account only. Household data kept for co-parents."
              >
                <UserX className="w-3.5 h-3.5 mr-1.5" /> Delete user
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onDelete(true)}
                className="border-[#E8B8B2] text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm h-9 px-3"
                data-testid={`admin-access-delete-cascade-${u.id}`}
                title="Delete user AND their household data (if they're the only member)."
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

const DURATION_PRESETS = [
  { key: "7", label: "7 days", days: 7 },
  { key: "30", label: "1 month", days: 30 },
  { key: "90", label: "3 months", days: 90 },
  { key: "180", label: "6 months", days: 180 },
  { key: "365", label: "12 months", days: 365 },
  { key: "custom", label: "Custom date", days: null },
  { key: "lifetime", label: "Lifetime", days: null },
];

const GrantDialog = ({ user, onClose, onDone }) => {
  const [mode, setMode] = useState("365");
  const [customDate, setCustomDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset state when the dialog re-opens for a different user.
  useEffect(() => {
    if (user) { setMode("365"); setCustomDate(""); setReason(""); }
  }, [user?.id]);

  const daysForCustom = useMemo(() => {
    if (mode !== "custom" || !customDate) return null;
    const target = new Date(customDate + "T00:00:00Z");
    const diff = target.getTime() - Date.now();
    return diff > 0 ? Math.ceil(diff / 86400000) : null;
  }, [mode, customDate]);

  const submit = async () => {
    if (!user) return;
    const body = { email: user.email, reason: reason.trim() || undefined };
    if (mode === "lifetime") {
      body.lifetime = true;
    } else if (mode === "custom") {
      if (!daysForCustom) { toast.error("Pick a future date"); return; }
      body.days = daysForCustom;
    } else {
      body.days = Number(mode);
    }
    setBusy(true);
    try {
      await api.post("/admin/complimentary", body);
      toast.success("Premium granted");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't grant");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="admin-access-grant-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Grant Premium access</DialogTitle>
          <DialogDescription className="text-[#2E3A3F]">
            {user?.email} — this bypasses Stripe and never triggers billing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-[0.08em] text-[#6F828A] mb-2">Duration</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {DURATION_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setMode(p.key)}
                  className={`px-3 py-2 text-sm border rounded-sm transition-colors text-left ${mode === p.key ? "border-[#1F9FA4] bg-[#E0F2F2] text-[#116B6E] font-medium" : "border-[#D9E1E3] bg-white text-[#2E3A3F] hover:bg-[#EEF3F4]"}`}
                  data-testid={`admin-access-duration-${p.key}`}
                >
                  {p.key === "lifetime" ? <><InfinityIcon className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> {p.label}</> : p.label}
                </button>
              ))}
            </div>
          </div>
          {mode === "custom" && (
            <div>
              <label className="block text-xs uppercase tracking-[0.08em] text-[#6F828A] mb-1">Expires on</label>
              <Input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                data-testid="admin-access-custom-date"
              />
              {daysForCustom && (
                <div className="text-[11px] text-[#6F828A] mt-1">≈ {daysForCustom} days from today</div>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs uppercase tracking-[0.08em] text-[#6F828A] mb-1">Reason <span className="normal-case tracking-normal text-[#6F828A]">(optional — recorded in audit log)</span></label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Beta tester, partner, personal account"
              maxLength={200}
              data-testid="admin-access-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy || (mode === "custom" && !daysForCustom)}
            className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm"
            data-testid="admin-access-grant-submit"
          >
            {busy ? "Granting…" : "Grant Premium"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ExtendDialog = ({ user, onClose, onDone }) => {
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) setDays(30); }, [user?.id]);

  const submit = async () => {
    if (!user) return;
    setBusy(true);
    try {
      await api.post("/admin/complimentary/extend", { email: user.email, add_days: Number(days) });
      toast.success(`Extended by ${days} days`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't extend");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-md" data-testid="admin-access-extend-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Extend Premium</DialogTitle>
          <DialogDescription className="text-[#2E3A3F]">
            Add more days to {user?.email}&apos;s current access. Time is added from the existing expiry.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            {[7, 30, 90, 365].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-xs border rounded-sm ${days === d ? "border-[#1F9FA4] bg-[#E0F2F2] text-[#116B6E] font-medium" : "border-[#D9E1E3] bg-white text-[#2E3A3F] hover:bg-[#EEF3F4]"}`}
              >
                +{d}d
              </button>
            ))}
          </div>
          <Input
            type="number"
            min={1}
            max={3650}
            value={days}
            onChange={(e) => setDays(Math.max(1, Math.min(3650, Number(e.target.value) || 1)))}
            data-testid="admin-access-extend-days"
          />
          {user?.access_expires_at && (
            <div className="text-xs text-[#6F828A]">
              Currently expires: <span className="font-mono-data text-[#2E3A3F]">{formatDT(user.access_expires_at)}</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy || days < 1}
            className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm"
            data-testid="admin-access-extend-submit"
          >
            {busy ? "Extending…" : `Extend by ${days} days`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ----- Flag badges -----
const _BADGE_TONES = {
  green: "bg-[#DCEFDC] text-[#1E5A1E] border-[#A9D3A9]",
  amber: "bg-[#FDF4E2] text-[#8F6B1F] border-[#E6CFA2]",
  red: "bg-[#FBEFED] text-[#8C2A22] border-[#E8B8B2]",
  grey: "bg-[#F0EFEE] text-[#6F828A] border-[#CFD6D8]",
};

const FlagBadge = ({ tone = "grey", icon: Icon, label, testid }) => (
  <span
    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border ${_BADGE_TONES[tone] || _BADGE_TONES.grey}`}
    data-testid={testid}
  >
    {Icon && <Icon className="w-3 h-3" />}
    {label}
  </span>
);

// ----- Impersonation dialog -----
// Required free-text reason. Disables the "Start impersonation" button
// until the admin has typed at least 5 characters of justification —
// this is the audit-log entry's most important field. Backend
// re-enforces the 3-character minimum.
const ImpersonateDialog = ({ user, onClose, onConfirmed }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [user]);
  if (!user) return null;
  const enabled = reason.trim().length >= 5 && !busy;
  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="admin-impersonate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#4E3877]" />
            Impersonate {user.email}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6F828A]">
            You&apos;ll be signed in as this user for up to 30 minutes. A
            persistent banner will show you&apos;re impersonating; admin
            endpoints will be disabled. Both the start and the end of
            the session are recorded in the admin audit log alongside
            your reason.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          <label className="block text-[11px] uppercase tracking-[0.1em] text-[#6F828A]">
            Reason <span className="lowercase tracking-normal text-[#A11C2C]">(required, min 5 characters)</span>
          </label>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. User reported missing receipts in court bundle export #4782"
            data-testid="admin-impersonate-reason"
          />
        </div>
        <DialogFooter className="mt-4">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            type="button"
            onClick={async () => {
              setBusy(true);
              try { await onConfirmed?.(reason.trim()); }
              finally { setBusy(false); }
            }}
            disabled={!enabled}
            className="bg-[#4E3877] hover:bg-[#3B2A5E] text-white rounded-sm disabled:opacity-50"
            data-testid="admin-impersonate-confirm"
          >
            {busy ? "Starting…" : "Start impersonation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// v77.99.115 — Payment-integrity remediation modal.
//
// Wraps the admin-only endpoint `POST /api/admin/maintenance/remediate-double-credits`
// in a dry-run → review → apply flow:
//
//   1. On open, the modal kicks off a dry-run for `user.email`. The
//      response lists every expense in that user's household where
//      BOTH (a) a payment-credit is allocated AND (b) a non-voided
//      direct payment exists (the iter114 double-credit signature).
//   2. The admin reviews the per-expense breakdown — title, date,
//      amount, credit-allocated total, and which specific payments
//      would be voided.
//   3. If the plan looks right, "Apply remediation" fires a 2nd call
//      with apply=true. The endpoint voids each duplicate direct
//      payment (mark `voided=True` + remediation metadata; record
//      stays in the array for audit) and recomputes the expense
//      status.
//   4. A green confirmation summary shows what was changed.
//
// All state lives in this component so closing the modal cleanly
// discards everything — re-opening starts a fresh dry-run.
const PaymentIntegrityModal = ({ user, open, onClose }) => {
  const [phase, setPhase] = useState("idle"); // idle | loading | preview | applying | applied | error
  const [plan, setPlan] = useState(null); // { summary, actions }
  const [applied, setApplied] = useState(null);
  const [err, setErr] = useState("");
  const [limit, setLimit] = useState(6);

  // Fetch a fresh dry-run every time the modal opens for a new user.
  useEffect(() => {
    if (!open || !user?.email) return;
    let cancelled = false;
    setPhase("loading");
    setPlan(null);
    setApplied(null);
    setErr("");
    (async () => {
      try {
        const { data } = await api.post(
          "/admin/maintenance/remediate-double-credits",
          { target_email: user.email, limit, apply: false },
        );
        if (cancelled) return;
        setPlan(data);
        setPhase("preview");
      } catch (e) {
        if (cancelled) return;
        setErr(e.response?.data?.detail || e.message || "Couldn't run dry-run");
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
    // We intentionally re-fetch only when the user identity OR the
    // requested limit changes. `open` re-running is fine — we want a
    // fresh scan every time the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.email, user?.id, limit]);

  const doApply = async () => {
    if (!user?.email) return;
    setPhase("applying");
    setErr("");
    try {
      const { data } = await api.post(
        "/admin/maintenance/remediate-double-credits",
        { target_email: user.email, limit, apply: true },
      );
      setApplied(data);
      setPhase("applied");
      toast.success(
        `Remediated ${data.summary?.matched_count ?? 0} expense(s) — `
        + `voided ${data.summary?.would_void_payments ?? 0} duplicate payment(s).`
      );
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Couldn't apply remediation");
      setPhase("preview");
    }
  };

  const actions = plan?.actions || [];
  const summary = plan?.summary || {};
  const appliedActions = applied?.actions || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-integrity-modal">
        <DialogHeader>
          <DialogTitle className="text-[#2E3A3F]">Payment integrity check</DialogTitle>
          <DialogDescription className="text-[#6F828A]">
            Scans <span className="font-mono-data">{user?.email}</span>&apos;s household for expenses that have
            been double-counted (a payment credit allocated to an expense, AND a direct payment recorded for the same money).
            The fix voids the duplicate direct payment; the credit allocation remains as the truthful settlement.
          </DialogDescription>
        </DialogHeader>

        {phase === "loading" && (
          <div className="py-8 text-center text-sm text-[#6F828A]" data-testid="admin-integrity-loading">
            <RefreshCw className="w-4 h-4 mx-auto mb-2 animate-spin" />
            Scanning expenses…
          </div>
        )}

        {phase === "error" && (
          <div className="border border-[#E8B8B2] bg-[#FBEFED] text-[#8C2A22] p-3 rounded-sm text-sm" data-testid="admin-integrity-error">
            {typeof err === "string" ? err : JSON.stringify(err)}
          </div>
        )}

        {(phase === "preview" || phase === "applying") && (
          <div className="space-y-3" data-testid="admin-integrity-preview">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div className="text-[13px] text-[#2E3A3F]">
                <span className="font-semibold tabular-nums">{summary.matched_count ?? 0}</span> double-counted expense(s) found
                {(summary.matched_count ?? 0) > 0 && (
                  <span className="text-[#6F828A]">
                    {" "}— {summary.would_void_payments ?? 0} duplicate payment(s) would be voided
                  </span>
                )}
              </div>
              <label className="text-[12px] text-[#6F828A] inline-flex items-center gap-2">
                Scan up to
                <Input
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(Math.max(1, Math.min(100, Number(e.target.value) || 6)))}
                  min={1}
                  max={100}
                  className="h-7 w-20 text-[12px]"
                  data-testid="admin-integrity-limit"
                />
                expenses
              </label>
            </div>

            {actions.length === 0 ? (
              <div className="border border-[#A9D3A9] bg-[#DCEFDC] text-[#1E5A1E] p-3 rounded-sm text-sm" data-testid="admin-integrity-clean">
                No double-counted expenses found for this household. Nothing to remediate.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="admin-integrity-actions">
                {actions.map((a) => (
                  <li
                    key={a.expense_id}
                    className="border border-[#D9E1E3] bg-[#F8FAFB] rounded-sm p-3"
                    data-testid={`admin-integrity-action-${a.expense_id}`}
                  >
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-medium text-[#2E3A3F] truncate">{a.title || "(untitled expense)"}</div>
                        <div className="text-[11px] text-[#6F828A] font-mono-data">
                          {a.date} · {a.currency || ""} {Number(a.amount || 0).toFixed(2)}
                        </div>
                      </div>
                      <div className="text-[11px] text-[#6F828A] text-right">
                        Credit allocated: <span className="font-mono-data tabular-nums text-[#116B6E]">{Number(a.credit_allocated_total || 0).toFixed(2)}</span><br />
                        Direct payments to void: <span className="font-mono-data tabular-nums text-[#8C2A22]">{(a.payments_to_void || []).length}</span>
                      </div>
                    </div>
                    {(a.payments_to_void || []).length > 0 && (
                      <ul className="mt-2 space-y-1 text-[12px]">
                        {a.payments_to_void.map((p) => (
                          <li key={p.id} className="flex flex-wrap items-baseline gap-x-2 text-[#6F828A]">
                            <span className="font-mono-data text-[#2E3A3F]">{Number(p.amount || 0).toFixed(2)}</span>
                            <span>{p.method || ""}</span>
                            {p.reference && <span className="italic">ref {p.reference}</span>}
                            <span>· {p.date}</span>
                            <span>· logged by {p.recorded_by_name || "—"}</span>
                            <span className={`text-[10px] uppercase tracking-[0.08em] px-1 border rounded-sm ${p.confirmed ? "border-[#A7D8DA] text-[#116B6E] bg-[#D6F0EF]" : "border-[#CFD6D8] text-[#6F828A] bg-[#F0EFEE]"}`}>
                              {p.confirmed ? "confirmed" : "unconfirmed"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {phase === "applied" && (
          <div className="space-y-3" data-testid="admin-integrity-applied">
            <div className="border border-[#A9D3A9] bg-[#DCEFDC] text-[#1E5A1E] p-3 rounded-sm text-sm">
              ✓ Remediated <span className="font-semibold">{applied?.summary?.matched_count ?? 0}</span> expense(s) — voided{" "}
              <span className="font-semibold">{applied?.summary?.would_void_payments ?? 0}</span> duplicate payment(s).
              The household&apos;s net balance is now correct.
            </div>
            {appliedActions.length > 0 && (
              <ul className="space-y-1" data-testid="admin-integrity-applied-actions">
                {appliedActions.map((a) => (
                  <li
                    key={a.expense_id}
                    className="border border-[#D9E1E3] bg-white rounded-sm p-2 text-[12px]"
                  >
                    <span className="font-medium text-[#2E3A3F]">{a.title}</span>
                    <span className="text-[#6F828A]"> — voided {a.voided_count ?? 0}, new status: </span>
                    <span className="font-medium capitalize">{a.new_status || "—"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter>
          {(phase === "preview" || phase === "applying") && actions.length > 0 && (
            <Button
              type="button"
              onClick={doApply}
              disabled={phase === "applying"}
              className="bg-[#C0291D] hover:bg-[#8C2A22] text-white rounded-sm disabled:opacity-50"
              data-testid="admin-integrity-apply"
            >
              {phase === "applying" ? "Applying…" : `Apply remediation (void ${summary.would_void_payments ?? 0} payment${(summary.would_void_payments ?? 0) === 1 ? "" : "s"})`}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F8FAFB] rounded-sm"
            data-testid="admin-integrity-close"
          >
            {phase === "applied" ? "Done" : "Cancel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
